import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Compass, MailQuestion, Sparkles, Users2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Empty } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { relTime } from "@/lib/time";

/**
 * Recruiting dashboard. Reads /admin/api/recruiting/* and renders the actual
 * server response shape (see routes_recruiting.py for the source of truth).
 *
 * Field mapping (response → UI):
 *   discovery_runs:  rule_name, started_at, finished_at, candidates_kept, error
 *   candidates:      ig_handle ?? source_handle, source, uv_score, status, bio, rejected_reason
 *   recruiter_outreach: id, recruiter_handle, candidate_handle, sent_at, last_inbound_at, outcome
 */
export function Recruiting() {
  const runsQ = useQuery({ queryKey: ["recruit", "runs"], queryFn: api.discoveryRuns });
  const candidatesQ = useQuery({ queryKey: ["recruit", "candidates"], queryFn: api.discoveryCandidates });
  const outreachQ = useQuery({ queryKey: ["recruit", "outreach"], queryFn: api.outreach });

  const runs7d = (runsQ.data ?? []).filter((r) => {
    const t = r.started_at ? new Date(r.started_at).getTime() : 0;
    return t > Date.now() - 7 * 24 * 3600 * 1000;
  }).length;

  const scoredCount = (candidatesQ.data ?? []).filter((c) => c.status === "scored").length;
  const totalCandidates = (candidatesQ.data ?? []).length;

  const inFlight = (outreachQ.data ?? []).filter(
    (o) => o.outcome === "pending" || o.outcome === "sent",
  ).length;
  const replies = (outreachQ.data ?? []).filter((o) => !!o.last_inbound_at).length;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Recruiting</h1>
        <p className="text-xs text-muted-foreground">
          Discovery, scoring, and outreach to candidate creators.
        </p>
      </header>

      <div className="grid gap-3 md:grid-cols-4">
        <Stat label="Runs (7d)" value={String(runs7d)} icon={<Compass />} />
        <Stat
          label="Candidates"
          value={`${scoredCount}/${totalCandidates}`}
          hint={`${scoredCount} scored, ${totalCandidates - scoredCount} rejected`}
          icon={<Users2 />}
        />
        <Stat label="Outreach in flight" value={String(inFlight)} icon={<MailQuestion />} />
        <Stat label="Replies" value={String(replies)} icon={<Sparkles />} />
      </div>

      <Tabs defaultValue="runs">
        <TabsList>
          <TabsTrigger value="runs">Discovery runs</TabsTrigger>
          <TabsTrigger value="candidates">Candidates</TabsTrigger>
          <TabsTrigger value="outreach">Outreach</TabsTrigger>
        </TabsList>

        <TabsContent value="runs">
          <RunsTab />
        </TabsContent>
        <TabsContent value="candidates">
          <CandidatesTab />
        </TabsContent>
        <TabsContent value="outreach">
          <OutreachTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string;
  hint?: string;
  icon?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 px-4 py-3">
        <div className="text-muted-foreground [&_svg]:h-3.5 [&_svg]:w-3.5">{icon}</div>
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="tnum text-lg font-semibold">{value}</div>
          {hint ? <div className="text-2xs text-muted-foreground">{hint}</div> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function statusFromRun(r: {
  finished_at: string | null;
  error: string | null;
  candidates_kept: number | null;
}): { label: string; tone: "success" | "danger" | "info" | "warning" } {
  if (r.error) return { label: "error", tone: "danger" };
  if (!r.finished_at) return { label: "running", tone: "info" };
  if ((r.candidates_kept ?? 0) === 0) return { label: "complete · 0 kept", tone: "warning" };
  return { label: "complete", tone: "success" };
}

function RunsTab() {
  const q = useQuery({ queryKey: ["recruit", "runs"], queryFn: api.discoveryRuns });
  if (q.isLoading) return <Skeleton className="h-32 w-full" />;
  const runs = q.data ?? [];
  if (!runs.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No discovery runs yet</CardTitle>
          <CardDescription>
            Runs appear here once the scheduler ticks (every 15 min) or you kick one manually.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-1.5 text-xs text-muted-foreground">
            <li>1. Define a discovery rule (hashtag, geo, follower band).</li>
            <li>2. Worker runs per cadence; writes candidates with scores.</li>
            <li>3. Top scored handles show on the Candidates tab.</li>
            <li>4. Outreach drafts queue once you assign a recruiter account.</li>
          </ol>
        </CardContent>
      </Card>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Rule</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Posts</TableHead>
          <TableHead className="text-right">Authors</TableHead>
          <TableHead className="text-right">Kept</TableHead>
          <TableHead className="text-right">Cost</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((r) => {
          const s = statusFromRun(r as { finished_at: string | null; error: string | null; candidates_kept: number | null });
          return (
            <TableRow key={r.run_id}>
              <TableCell className="text-xs">{(r as { rule_name?: string }).rule_name ?? r.rule_id}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {r.started_at ? relTime(r.started_at) : "—"}
              </TableCell>
              <TableCell>
                <Badge tone={s.tone}>{s.label}</Badge>
              </TableCell>
              <TableCell className="tnum text-right text-xs">{r.posts_pulled ?? 0}</TableCell>
              <TableCell className="tnum text-right text-xs">{r.authors_unique ?? 0}</TableCell>
              <TableCell className="tnum text-right text-xs font-semibold">{r.candidates_kept ?? 0}</TableCell>
              <TableCell className="tnum text-right text-xs">${((r.cost_cents ?? 0) / 100).toFixed(2)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function CandidatesTab() {
  const q = useQuery({ queryKey: ["recruit", "candidates"], queryFn: api.discoveryCandidates });
  const [filter, setFilter] = React.useState<"scored" | "rejected" | "all">("scored");
  if (q.isLoading) return <Skeleton className="h-32 w-full" />;
  const all = q.data ?? [];
  const items = all.filter((c) => filter === "all" || c.status === filter);

  if (!all.length) {
    return (
      <Empty
        title="No candidates yet"
        hint="Once a run completes, all discovered handles appear here — both scored and rejected. Filter by status to compare."
      />
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5 text-xs">
        {(["scored", "rejected", "all"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded px-2 py-1 ${
              filter === f
                ? "bg-foreground text-background"
                : "border border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {f} ({all.filter((c) => f === "all" || c.status === f).length})
          </button>
        ))}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Handle</TableHead>
            <TableHead className="text-right">Followers</TableHead>
            <TableHead className="text-right">Eng.</TableHead>
            <TableHead className="text-right">UV score</TableHead>
            <TableHead>Tier</TableHead>
            <TableHead>Archetype</TableHead>
            <TableHead className="hidden lg:table-cell">Read</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((c) => {
            const handle = c.ig_handle ?? c.source_handle ?? "—";
            const ig = c.ig_handle;
            const score = c.uv_score;
            const er = c.engagement_rate;
            const followers = c.followers;
            const tier = c.predicted_tier ?? null;
            const summary = c.summary;
            const rejectedReason = c.rejected_reason;
            const detail =
              c.status === "rejected"
                ? rejectedReason ?? "—"
                : summary ?? (c.bio ?? "").slice(0, 140) ?? "—";
            return (
              <TableRow key={c.candidate_id} title={c.bio ?? ""}>
                <TableCell className="text-xs">
                  <div className="flex items-center gap-2">
                    {ig ? (
                      <a
                        href={`https://instagram.com/${ig}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-foreground hover:underline"
                      >
                        @{handle}
                      </a>
                    ) : (
                      <span>@{handle}</span>
                    )}
                    {c.detected_gender && c.detected_gender !== "unknown" ? (
                      <span className="text-2xs uppercase text-muted-foreground">
                        {c.detected_gender === "female" ? "♀" : c.detected_gender === "male" ? "♂" : "·"}
                      </span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="tnum text-right text-xs">
                  {followers != null ? followers.toLocaleString() : "—"}
                </TableCell>
                <TableCell className="tnum text-right text-xs">
                  {er != null ? `${(er * 100).toFixed(1)}%` : "—"}
                </TableCell>
                <TableCell className="tnum text-right text-xs">
                  {score != null ? (
                    <span
                      className={
                        score >= 8.4
                          ? "font-semibold text-emerald-400"
                          : score >= 7.0
                            ? "font-semibold text-emerald-300"
                            : score >= 5.0
                              ? "font-medium text-amber-300"
                              : "text-muted-foreground"
                      }
                    >
                      {score.toFixed(1)}
                    </span>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  {tier ? (
                    <Badge
                      tone={
                        tier === "TOP EARNER"
                          ? "success"
                          : tier === "HIGH PERFORMER"
                            ? "success"
                            : tier === "MID TIER"
                              ? "info"
                              : tier === "LOW POTENTIAL"
                                ? "muted"
                                : "muted"
                      }
                    >
                      {tier.toLowerCase()}
                    </Badge>
                  ) : c.status === "rejected" ? (
                    <Badge tone="muted">rejected</Badge>
                  ) : (
                    <Badge tone="muted">unscored</Badge>
                  )}
                </TableCell>
                <TableCell className="text-2xs text-muted-foreground">
                  {c.archetype ?? "—"}
                </TableCell>
                <TableCell className="hidden max-w-xl truncate text-2xs text-muted-foreground lg:table-cell">
                  {detail}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function OutreachTab() {
  const q = useQuery({ queryKey: ["recruit", "outreach"], queryFn: api.outreach });
  if (q.isLoading) return <Skeleton className="h-32 w-full" />;
  const items = q.data ?? [];
  if (!items.length) {
    return (
      <Empty
        title="Outreach inbox is empty"
        hint="Outbound DMs from recruiter accounts will surface here once outreach drafts are approved and sent."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Recruiter</TableHead>
          <TableHead>Candidate</TableHead>
          <TableHead>Sent</TableHead>
          <TableHead>Last reply</TableHead>
          <TableHead>Outcome</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((o) => {
          const recruiter = (o as { recruiter_handle?: string }).recruiter_handle ?? "—";
          const candidate = (o as { candidate_handle?: string }).candidate_handle ?? "—";
          const outcome = (o as { outcome?: string }).outcome ?? "pending";
          const sent = (o as { sent_at?: string | null }).sent_at;
          const inbound = (o as { last_inbound_at?: string | null }).last_inbound_at;
          return (
            <TableRow key={(o as { id: string }).id}>
              <TableCell className="font-mono text-xs">{recruiter}</TableCell>
              <TableCell className="text-xs">@{candidate}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {sent ? relTime(sent) : "—"}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {inbound ? relTime(inbound) : "—"}
              </TableCell>
              <TableCell>
                <Badge
                  tone={
                    outcome === "converted"
                      ? "success"
                      : outcome === "blocked" || outcome === "no_reply"
                        ? "danger"
                        : outcome === "replied" || outcome === "escalated_human"
                          ? "warning"
                          : "info"
                  }
                >
                  {outcome}
                </Badge>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
